import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getStockMovements } from '../../services/materials';
import { onQuerySuccess } from '../../utils/queryResponse';
import { rsdFormat, toLocalDateString } from '../../utils/helper';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const BoxIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

const typeColor = {
    ADDED: 'var(--good)',
    RESERVED: 'var(--brand)',
    USED: 'var(--muted)',
    RETURNED: 'var(--warn)',
    DAMAGED: 'var(--bad)',
    ADJUSTED: 'var(--muted)',
};

const MaterialDetail = ({ material, onClose }) => {
    const { t } = useTranslation();

    const [movements, setMovements] = useState([])

    const { data } = useQuery(
        ['stock-movements', material.id],
        () => getStockMovements(material.id, { limit: 50 }),
        { enabled: !!material.id }
    )

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setMovements(result.data || result)
        })
    }, [data])

    return (
        <SbrDrawer
            show={true}
            onHide={onClose}
            title={material.name}
            subtitle={material.sku || material.unit}
            icon={<BoxIcon />}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Info tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                    <div className="card-sbr" style={{ padding: '14px' }}>
                        <div className="sub" style={{ fontSize: '11px', fontWeight: 700 }}>{t('materialsPage.sellingPriceLabel')}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700 }}>{rsdFormat(material.sellingPrice)}</div>
                    </div>
                    <div className="card-sbr" style={{ padding: '14px' }}>
                        <div className="sub" style={{ fontSize: '11px', fontWeight: 700 }}>{t('materialsPage.currentStockLabel')}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700 }}>{material.currentStock} {material.unit}</div>
                    </div>
                    {material.purchasePrice != null && (
                        <div className="card-sbr" style={{ padding: '14px' }}>
                            <div className="sub" style={{ fontSize: '11px', fontWeight: 700 }}>{t('materialsPage.purchasePriceLabel')}</div>
                            <div style={{ fontSize: '16px', fontWeight: 700 }}>{rsdFormat(material.purchasePrice)}</div>
                        </div>
                    )}
                    <div className="card-sbr" style={{ padding: '14px' }}>
                        <div className="sub" style={{ fontSize: '11px', fontWeight: 700 }}>{t('materialsPage.minStockLabel')}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700 }}>{material.minimumStock} {material.unit}</div>
                    </div>
                </div>

                {/* Stock movements */}
                <div>
                    <div className="sub mb-2" style={{ fontWeight: 600, fontSize: '12px' }}>{t('materialsPage.stockMovements')}</div>
                    {movements.length === 0 ? (
                        <div className="card-sbr" style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)' }}>
                            {t('materialsPage.noMovements')}
                        </div>
                    ) : (
                        <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ padding: '2px 16px' }}>
                                {movements.map((mv) => (
                                    <div key={mv.id} className="row-sbr">
                                        <div className="info">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px',
                                                    color: typeColor[mv.type], background: 'var(--surface)',
                                                    border: '1px solid var(--line)', borderRadius: '7px', padding: '2px 8px',
                                                }}>
                                                    {t(`materialsPage.movementType_${mv.type}`, { defaultValue: mv.type })}
                                                </span>
                                                <span className="nm">
                                                    {mv.quantity > 0 ? '+' : ''}{mv.quantity} {material.unit}
                                                </span>
                                            </div>
                                            <div className="sub">
                                                {toLocalDateString(mv.createdAt)}
                                                {mv.createdBy && <> · {mv.createdBy.firstName} {mv.createdBy.lastName}</>}
                                                {mv.note && <> · {mv.note}</>}
                                            </div>
                                        </div>
                                        <div className="rt" style={{ fontSize: '12px', color: 'var(--muted)' }}>
                                            {mv.previousStock} → {mv.newStock}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SbrDrawer>
    );
};

export default MaterialDetail;
